import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, AppUser, MealSlot, WeekDto } from "../api/client";
import {
  DAY_LABELS_FULL,
  MONTHS_CA,
  SLOT_ROWS,
  formatWeekRange,
  isoMondayString,
} from "../constants";

const MEAL_CLASS: Record<MealSlot, string> = {
  breakfast1: "m-breakfast",
  breakfast2: "m-breakfast",
  lunch: "m-lunch",
  snack: "m-snack",
  dinner: "m-dinner",
};

function shortLabel(user: { name: string; printLabel: string }): string {
  return user.printLabel || user.name.slice(0, 2);
}

export function WeekPrint() {
  const [params] = useSearchParams();
  const monday = params.get("monday") ?? isoMondayString();

  const [week, setWeek] = useState<WeekDto | null>(null);
  const [users, setUsers] = useState<AppUser[]>([]);

  useEffect(() => {
    Promise.all([
      api.get<AppUser[]>("/users"),
      api.get<WeekDto>(`/weeks/${monday}`),
    ]).then(([u, w]) => {
      setUsers(u);
      setWeek(w);
    });
  }, [monday]);

  const slotMap = new Map<string, WeekDto["slots"][0]>();
  week?.slots.forEach((s) => slotMap.set(`${s.day}-${s.mealSlot}`, s));

  const allUserIds = users.map((u) => u.id);
  const range = formatWeekRange(monday);
  const rangeStr = `${range.label} de ${range.year}`;

  const dayDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + i);
    return { num: d.getUTCDate(), month: MONTHS_CA[d.getUTCMonth()] };
  });

  return (
    <>
      <PrintPageStyles />

      <div className="pr-toolbar">
        <span className="pr-tb-brand">
          Nara<span>Menu</span> · menú setmanal
        </span>
        <div className="pr-tb-sep" />
        <span className="pr-tb-hint">A4 horitzontal · usa «Desa com a PDF» al diàleg d'impressió</span>
        <button className="pr-btn pr-btn-primary" onClick={() => window.print()}>
          <PrinterIcon />
          Desa com a PDF / Imprimeix
        </button>
      </div>

      <div className="pr-stage">
        <div className="pr-sheet">
          <header className="pr-head">
            <div className="pr-brand-wm">NaraMenu</div>
            <h1 className="pr-h1">Menú</h1>
            <div className="pr-range">{rangeStr}</div>
          </header>

          <table className="pr-grid">
            <thead>
              <tr>
                <th className="pr-corner" />
                {dayDates.map((dd, di) => (
                  <th key={di} className="pr-dayhd">
                    <span className="pr-dayname">{DAY_LABELS_FULL[di + 1]}</span>
                    <span className="pr-daydate">
                      {dd.num} {dd.month}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SLOT_ROWS.map((row) => (
                <tr key={row.slot}>
                  <th className={`pr-rowhd ${MEAL_CLASS[row.slot]}`}>
                    <span>{row.label}</span>
                  </th>
                  {Array.from({ length: 7 }, (_, di) => {
                    const slot = slotMap.get(`${di + 1}-${row.slot}`);
                    const cards = slot?.cards ?? [];
                    return (
                      <td key={di} className="pr-cell">
                        {cards.length === 0 ? (
                          <span className="pr-empty">·</span>
                        ) : (
                          cards.map((card) => {
                            const eaterIds = card.users.map((u) => u.userId);
                            const excluded = row.slot === "snack"
                              ? []
                              : allUserIds.filter((id) => !eaterIds.includes(id));
                            const showEaters = excluded.length >= 2;
                            return (
                              <div
                                key={card.id}
                                className={`pr-rec ${MEAL_CLASS[row.slot]}`}
                              >
                                <span className="pr-rec-name">{card.recipe.name}</span>
                                {excluded.length === 1 && (
                                  <span className="pr-excl">
                                    {excluded.map((id) => {
                                      const user = users.find((u) => u.id === id);
                                      if (!user) return null;
                                      return (
                                        <s key={id} title={`${user.name} no en menja`}>
                                          {shortLabel(user)}
                                        </s>
                                      );
                                    })}
                                  </span>
                                )}
                                {showEaters && (
                                  <span className="pr-excl">
                                    {eaterIds.map((id) => {
                                      const user = users.find((u) => u.id === id);
                                      if (!user) return null;
                                      return (
                                        <span key={id} title={`${user.name} en menja`}>
                                          {shortLabel(user)}
                                        </span>
                                      );
                                    })}
                                  </span>
                                )}
                              </div>
                            );
                          })
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function PrinterIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

function PrintPageStyles() {
  return (
    <style>{`
      .pr-toolbar, .pr-stage, .pr-sheet {
        --ink: #000000;
        --ink-soft: #111111;
        --muted: #444444;
        --faint: #555555;
        --line: #888888;
        --paper: #ffffff;
        --tint: #faf7f3;
        --accent: oklch(66% 0.17 47);
        --cross: #b04a30;
        --font-body: "Archivo Narrow", "Arial Narrow", system-ui, sans-serif;
        --font-brand: "Baloo 2", ui-rounded, system-ui, sans-serif;
      }

      .pr-toolbar {
        position: sticky; top: 0; z-index: 10;
        display: flex; align-items: center; gap: 14px;
        padding: 13px 22px;
        background: rgba(255,255,255,.92);
        backdrop-filter: blur(10px);
        border-bottom: 1px solid var(--line);
        font-family: var(--font-body);
      }
      .pr-tb-brand {
        font-family: var(--font-brand); font-weight: 700;
        font-size: 18px; letter-spacing: -.02em; color: var(--ink);
      }
      .pr-tb-brand span { color: var(--accent); }
      .pr-tb-sep { flex: 1; }
      .pr-tb-hint { font-size: 12.5px; font-weight: 600; color: var(--muted); }

      .pr-btn {
        display: inline-flex; align-items: center; gap: 7px; cursor: pointer;
        border: 1px solid var(--line); background: #fff; color: var(--ink);
        font-family: inherit; font-weight: 700; font-size: 13.5px;
        padding: 9px 15px; border-radius: 11px;
        box-shadow: 0 1px 2px rgba(40,30,20,.05);
      }
      .pr-btn:hover { background: var(--tint); }
      .pr-btn-primary {
        background: var(--accent); color: #fff; border-color: transparent;
      }
      .pr-btn-primary:hover { filter: brightness(.96); }

      .pr-stage {
        background: oklch(96% 0.008 75);
        display: flex; justify-content: center;
        padding: 30px 20px 60px;
        min-height: calc(100vh - 56px);
      }

      .pr-sheet {
        width: 297mm; min-height: 210mm;
        background: var(--paper);
        padding: 5mm;
        box-shadow: 0 10px 40px rgba(40,30,20,.14);
        border-radius: 3px;
        font-family: var(--font-body);
        color: var(--ink);
        -webkit-font-smoothing: antialiased;
      }

      .pr-head {
        display: flex; align-items: baseline;
        justify-content: space-between; gap: 24px;
        margin-bottom: 10px; padding-bottom: 9px;
      }
      .pr-brand-wm {
        flex: 1;
        font-family: var(--font-brand); font-weight: 600; font-size: 16px;
        letter-spacing: -.02em; color: var(--accent);
      }
      .pr-h1 {
        font-family: var(--font-brand); font-weight: 700; font-size: 26px;
        margin: 0; letter-spacing: -.01em; white-space: nowrap; text-align: center;
      }
      .pr-range {
        flex: 1; text-align: right; font-weight: 600; font-size: 14.5px;
        color: var(--muted);
      }

      .pr-grid { width: 100%; border-collapse: collapse; table-layout: fixed; }
      .pr-corner { width: 34px; border-bottom: 1.5px solid var(--line); }
      .pr-dayhd {
        padding: 3px 4px 9px; text-align: center;
        border-bottom: 1.5px solid var(--line);
      }
      .pr-dayname {
        display: block; font-weight: 700; font-size: 20px;
        letter-spacing: .01em; color: var(--accent);
      }
      .pr-daydate {
        display: block; font-weight: 600; font-size: 15px;
        color: var(--faint); margin-top: 1px;
      }
      .pr-rowhd {
        width: 34px; vertical-align: middle; text-align: center;
        color: var(--accent); font-weight: 600; font-size: 16px;
        letter-spacing: .03em;
        border-bottom: 1.5px solid var(--line); padding: 0;
      }
      .pr-rowhd span {
        display: inline-block;
        writing-mode: vertical-rl; transform: rotate(180deg);
      }
      .pr-cell {
        vertical-align: middle; padding: 8px 9px;
        height: 36mm; border-bottom: 1.5px solid var(--line);
      }
      .pr-empty {
        display: block; text-align: center;
        color: #d8cdc0; font-weight: 800; padding-top: 10px;
      }
      .pr-rec {
        border-radius: 6px;
        padding: 5px 7px; margin-bottom: 5px;
      }
      .pr-rec:last-child { margin-bottom: 0; }
      .pr-rec.m-breakfast .pr-rec-name,
      .pr-rec.m-snack .pr-rec-name {
        font-size: 15px; font-weight: 600; color: #000000;
      }
      .pr-rec-name {
        display: block; font-weight: 700; font-size: 18px;
        line-height: 1.18; color: #000000; text-align: center;
      }
      .pr-excl {
        display: flex; flex-wrap: wrap;
        justify-content: center; gap: 5px; margin-top: 4px;
      }
      .pr-excl s, .pr-excl span {
        font-weight: 600; font-size: 14px;
        color: #888888; text-decoration-color: #888888; text-decoration-thickness: 1.4px;
      }

      @media print {
        @page { size: A4 landscape; margin: 5mm; }
        body { background: #fff !important; }
        .pr-toolbar { display: none !important; }
        .topnav { display: none !important; }
        .pr-stage {
          padding: 0; min-height: 0; background: #fff;
        }
        .pr-sheet {
          width: auto; min-height: 0; padding: 0;
          box-shadow: none; border-radius: 0;
        }
      }
    `}</style>
  );
}
